<template>
  <div>
    <loading
      :active.sync="isLoading"
      color="#9de35c"
      loader="bars"
      :width="loadingSize"
      :height="loadingSize"
      :is-full-page="loadingFullPage"
    />
    
    <v-row class="mx-0 mb-2" align="center">
      <v-col cols="auto" class="py-1 py-sm-2">
        <v-btn text @click="goBack">
          <v-icon left>mdi-arrow-left</v-icon> Back to Groups
        </v-btn>
      </v-col>
      <v-spacer />
      <v-col class="py-1 py-sm-2 text-right">
        <app-btn @click="openEditDialog">
          <v-icon class="mr-1">mdi-pencil</v-icon> Edit
        </app-btn>
        <app-btn class="ml-2" @click="openMembersDialog">
          <v-icon class="mr-1">mdi-account-group</v-icon> Manage Members
        </app-btn>
        <app-btn class="ml-2" color="success" @click="openCommandDialog">
          <v-icon class="mr-1">mdi-send</v-icon> Send Command
        </app-btn>
      </v-col>
    </v-row>

    <!-- Group Info Card -->
    <material-card
      icon="mdi-group"
      icon-small
      color="primary"
      :title="group.name || 'Device Group'"
    >
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <div class="group-info-item">
              <span class="group-info-label">Description:</span>
              <span class="group-info-value">{{ group.description || 'No description' }}</span>
            </div>
            <div class="group-info-item">
              <span class="group-info-label">Owner Company:</span>
              <span class="group-info-value">{{ group.ownerCompanyName || 'All Companies (SuperUser)' }}</span>
            </div>
            <div class="group-info-item">
              <span class="group-info-label">Created:</span>
              <span class="group-info-value">{{ formatDate(group.createdAt) }}</span>
            </div>
            <div class="group-info-item">
              <span class="group-info-label">Last Updated:</span>
              <span class="group-info-value">{{ formatDate(group.updatedAt) }}</span>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="group-stats">
              <v-chip class="ma-1" color="primary" text-color="white">
                <v-icon left small>mdi-devices</v-icon>
                {{ effectiveDevices.length }} Total Devices
              </v-chip>
              <v-chip class="ma-1" color="success" text-color="white">
                <v-icon left small>mdi-check-circle</v-icon>
                {{ explicitDevices.length }} Explicit Members
              </v-chip>
              <v-chip v-if="companyDevices.length > 0" class="ma-1" color="info" text-color="white">
                <v-icon left small>mdi-domain</v-icon>
                {{ companyDevices.length }} From Companies
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </material-card>

    <v-expansion-panels v-model="wifiPanel" class="mt-4">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <span><v-icon left>mdi-wifi</v-icon> Group WiFi management</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <wifi-management v-if="groupId" :group-id="groupId" :company-id="wifiCompanyId" :active="wifiPanel === 0" />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Effective Devices Card -->
    <material-card
      icon="mdi-devices"
      icon-small
      color="success"
      title="Effective Devices"
      class="mt-4"
    >
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <v-text-field
              v-model="deviceSearch"
              append-icon="mdi-magnify"
              hide-details
              label="Search devices"
              single-line
            />
          </v-col>
        </v-row>
        <v-divider class="mt-1" />
        
        <v-data-table
          :headers="deviceHeaders"
          :items="effectiveDevices"
          :search="deviceSearch"
          :loading="loading"
          :items-per-page="10"
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': [5, 10, 15, 20],
          }"
        >
          <template v-slot:item.name="{ item }">
            {{ item.name || 'Unnamed Device' }}
          </template>
          
          <template v-slot:item.macAddress="{ item }">
            <code>{{ item.macAddress || 'N/A' }}</code>
          </template>
          
          <template v-slot:item.companyName="{ item }">
            {{ item.companyName || `Company #${item.companyId}` }}
          </template>
          
          <template v-slot:item.membership="{ item }">
            <v-chip v-if="item.explicitMember" x-small color="success" text-color="white">
              Explicit
            </v-chip>
            <v-chip v-if="item.includedByCompany" x-small color="info" text-color="white">
              By Company
            </v-chip>
          </template>
          
          <template v-slot:item.actions="{ item }">
            <v-btn icon small title="View Device" @click="viewDevice(item)">
              <v-icon small color="info">mdi-eye</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>

    <!-- Edit Dialog -->
    <v-dialog v-model="dialogEdit" max-width="700px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Edit Device Group</span>
        </v-card-title>
        
        <v-card-text>
          <v-container>
            <v-alert v-if="formError" dense text type="error" class="mb-3">
              {{ formError }}
            </v-alert>
            
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.name"
                  label="Group Name *"
                  :error-messages="nameError"
                  counter="100"
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editedItem.description"
                  label="Description"
                  counter="500"
                  rows="2"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogEdit = false">Cancel</v-btn>
          <v-btn color="primary" text :loading="saving" @click="save">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Manage Members Dialog -->
    <v-dialog v-model="dialogMembers" max-width="800px" scrollable>
      <v-card>
        <v-card-title>
          <span class="text-h5">Manage Group Members</span>
        </v-card-title>
        
        <v-card-text>
          <v-container>
            <v-alert v-if="membersError" dense text type="error" class="mb-3">
              {{ membersError }}
            </v-alert>
            
            <!-- SuperUser only: Company selector -->
            <v-row v-if="isSuperUser">
              <v-col cols="12">
                <v-autocomplete
                  v-model="membersEdit.companyIds"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  label="Include All Devices From Companies"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hint="All devices from selected companies will be included"
                  persistent-hint
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="membersEdit.deviceIds"
                  :items="availableDevices"
                  item-text="deviceName"
                  item-value="id"
                  label="Select Explicit Devices"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  :loading="loadingDevices"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.deviceName || item.name || 'Unnamed Device' }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.macAddress }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-divider class="my-4" />

            <!-- Current Members Summary -->
            <h4 class="text-subtitle-1 mb-2">Current Configuration</h4>
            <div class="current-members-summary">
              <p><strong>Explicit Devices:</strong> {{ (membersEdit.deviceIds || []).length }}</p>
              <p v-if="isSuperUser"><strong>Companies:</strong> {{ (membersEdit.companyIds || []).length }}</p>
              <p><strong>Total Effective Devices:</strong> {{ previewDeviceCount }}</p>
            </div>
          </v-container>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogMembers = false">Cancel</v-btn>
          <v-btn color="primary" text :loading="savingMembers" @click="saveMembers">
            Save Members
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Send Command Dialog -->
    <v-dialog v-model="dialogCommand" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Send Command to Group</span>
        </v-card-title>
        
        <v-card-text>
          <v-container>
            <v-alert v-if="commandError" dense text type="error" class="mb-3">
              {{ commandError }}
            </v-alert>
            
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="commandItem.type"
                  :items="commandTypeItems"
                  label="Command Type *"
                  :loading="loadingCommandTypes"
                  :error-messages="commandTypeError"
                  required
                  @change="onCommandTypeChange"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.text }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.description">
                        {{ item.description }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>
              <v-col v-if="selectedCommand" cols="12">
                <command-payload-form
                  v-if="dialogCommand"
                  :group-id="groupId"
                  v-model="commandPayloadValues"
                  :command="selectedCommand"
                  :option-sources="commandCatalog.optionSources"
                  :company-id="payloadCompanyId"
                  :wifi-company-id="wifiCompanyId"
                  :company-ids="payloadCompanyIds"
                  :disabled="sendingCommand"
                  :show-errors="showPayloadErrors"
                  @validity="payloadValid = $event"
                />
              </v-col>
              <v-col v-else-if="commandItem.type" cols="12">
                <v-alert dense text type="warning" class="mb-0">
                  The catalog has no payload schema for "{{ commandItem.type }}",
                  so it will be sent with no payload.
                </v-alert>
              </v-col>
              <v-col v-if="commandItem.type" cols="12">
                <v-divider class="mb-3" />
                <command-expiry-picker
                  v-model="commandExpiry"
                  :default-info="selectedExpiryInfo"
                  :disabled="sendingCommand"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCommand = false">Cancel</v-btn>
          <v-btn color="success" text :loading="sendingCommand" @click="sendCommand">
            Send Command
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Command Result Dialog -->
    <v-dialog v-model="dialogCommandResult" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Command Sent</span>
        </v-card-title>
        
        <v-card-text>
          <v-alert dense text type="success" class="mb-3">
            Command batch created successfully!
          </v-alert>
          
          <div v-if="commandResult">
            <p><strong>Batch ID:</strong> {{ commandResult.batchId }}</p>
            <p><strong>Target Devices:</strong> {{ commandResult.targetCount }}</p>
            <p><strong>Dispatched Immediately:</strong> {{ commandResult.dispatchedCount }}</p>
            <p><strong>Queued (offline):</strong> {{ commandResult.queuedCount }}</p>
            <p><strong>Failed:</strong> {{ commandResult.failedCount }}</p>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCommandResult = false">Close</v-btn>
          <v-btn color="primary" text @click="viewHistory">
            View History
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import MaterialCard from '@/components/MaterialCard'
import DeviceGroupsDataService from '@/services/DeviceGroupsDataService'
import { singleDeviceCompanyId } from '@/util/wifiManagement'
import CommandPayloadForm from '@/views/components/CommandPayloadForm.vue'
import CommandExpiryPicker from '@/views/components/CommandExpiryPicker.vue'
import WifiManagement from '@/views/components/WifiManagement.vue'
import { buildExpiryFields, buildPayloadForCommand, commandSelectItems, isPayloadIncomplete, normalizeCatalog } from '@/util/commandCatalog'
import { mapGetters } from 'vuex'

export default {
  name: 'DeviceGroupDetail',
  components: {
    Loading,
    MaterialCard,
    CommandPayloadForm,
    CommandExpiryPicker,
    WifiManagement,
  },
  
  data() {
    return {
      isLoading: false,
      loadingFullPage: true,
      loadingSize: 64,
      loading: false,
      loadingDevices: false,
      
      groupId: null,
      wifiPanel: null,
      group: {},
      effectiveDevices: [],
      companies: [],
      availableDevices: [],
      deviceSearch: '',
      
      deviceHeaders: [
        { text: 'Device Name', value: 'name', sortable: true },
        { text: 'MAC Address', value: 'macAddress', sortable: true },
        { text: 'Company', value: 'companyName', sortable: true },
        { text: 'Membership', value: 'membership', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      
      dialogEdit: false,
      dialogMembers: false,
      dialogCommand: false,
      dialogCommandResult: false,
      
      editedItem: {
        name: '',
        description: '',
      },
      formError: '',
      nameError: '',
      saving: false,
      
      membersEdit: {
        deviceIds: [],
        companyIds: [],
      },
      membersError: '',
      savingMembers: false,
      previewDeviceCount: 0,
      
      commandItem: {
        type: '',
        payload: null,
      },
      commandCatalog: { commandTypes: [], commands: [], commandsByType: {}, optionSources: {} },
      loadingCommandTypes: false,
      commandPayloadValues: {},
      payloadValid: true,
      showPayloadErrors: false,
      commandExpiry: { mode: 'default', ttlMinutes: null, expiresAt: null },
      commandError: '',
      commandTypeError: '',
      sendingCommand: false,
      commandResult: null,
    }
  },
  
  computed: {
    ...mapGetters('authUser', ['isSuperUser', 'currentCompanyId']),
    ...mapGetters('company', ['getCompanies']),
    
    explicitDevices() {
      return this.effectiveDevices.filter(d => d.explicitMember)
    },
    
    companyDevices() {
      return this.effectiveDevices.filter(d => d.includedByCompany)
    },

    commandTypeItems() {
      return commandSelectItems(this.commandCatalog).filter(item => item.value !== 'GetSavedWifiNetworks')
    },

    selectedCommand() {
      return this.commandCatalog.commandsByType[this.commandItem.type] || null
    },

    // Per-type expiry defaults, published alongside the descriptors.
    selectedExpiryInfo() {
      return (this.commandCatalog.expiryByType || {})[this.commandItem.type] || null
    },

    // Option sources are scoped by company; use the group's owner where it has one.
    wifiCompanyId() {
      return singleDeviceCompanyId(this.effectiveDevices)
    },

    payloadCompanyId() {
      return this.group.ownerCompanyId || this.currentCompanyId
    },

    // A group can span companies, and app lists are per company. Hand the form
    // every company it targets so option lists can be intersected.
    payloadCompanyIds() {
      return [...new Set(this.effectiveDevices.map(device => device.companyId).filter(Boolean))]
    },
  },
  
  mounted() {
    this.groupId = this.$route.params.id
    this.loadGroup()
    this.loadCommandTypes()
    this.loadCompanies()
  },
  
  methods: {
    async loadGroup() {
      this.loading = true
      try {
        const response = await DeviceGroupsDataService.get(this.groupId)
        this.group = response.data || {}
        this.effectiveDevices = this.group.effectiveDevices || []
      } catch (error) {
        this.$toast.error('Failed to load device group')
        console.error('Error loading group:', error)
        if (error.response?.status === 404) {
          this.$router.push('/device-groups')
        }
      } finally {
        this.loading = false
      }
    },
    
    async loadCompanies() {
      try {
        if (this.isSuperUser) {
          await this.$store.dispatch('company/loadCompanies', 0)
        }
        this.companies = this.getCompanies || []
      } catch (error) {
        console.error('Error loading companies:', error)
        this.companies = []
      }
    },
    
    async loadCommandTypes() {
      this.loadingCommandTypes = true
      try {
        const response = await DeviceGroupsDataService.getCommandTypes()
        this.commandCatalog = normalizeCatalog(response.data)
      } catch (error) {
        console.error('Error loading command catalog:', error)
        this.commandCatalog = { commandTypes: [], commands: [], commandsByType: {}, optionSources: {} }
      } finally {
        this.loadingCommandTypes = false
      }
    },

    onCommandTypeChange() {
      // Each command declares its own fields; start its payload from scratch.
      this.commandPayloadValues = {}
      this.showPayloadErrors = false
      this.commandTypeError = ''
      // Expiry defaults are per type, so fall back to the server default.
      this.commandExpiry = { mode: 'default', ttlMinutes: null, expiresAt: null }
    },

    // Devices come back with `name`, but the picker keys its text off
    // `deviceName` -- without it the selection chips render empty.
    normalizeDevices(data) {
      if (!Array.isArray(data)) return []
      
      return data.map(device => ({
        ...device,
        deviceName: device.deviceName || device.name || device.macAddress || `Device #${device.id}`,
      }))
    },
    
    async loadAllDevices() {
      this.loadingDevices = true
      try {
        const response = await this.$http.get('Company/DevicePaginationByCompany', {
          params: { 
            companyId: this.group.ownerCompanyId || this.currentCompanyId,
            page: 1,
            limit: 1000 
          }
        })
        this.availableDevices = this.normalizeDevices(response.data)
      } catch (error) {
        console.error('Error loading devices:', error)
      } finally {
        this.loadingDevices = false
      }
    },
    
    openEditDialog() {
      this.editedItem = {
        name: this.group.name || '',
        description: this.group.description || '',
      }
      this.formError = ''
      this.nameError = ''
      this.dialogEdit = true
    },
    
    validateForm() {
      this.nameError = ''
      let isValid = true
      
      if (!this.editedItem.name || this.editedItem.name.trim() === '') {
        this.nameError = 'Name is required'
        isValid = false
      } else if (this.editedItem.name.length > 100) {
        this.nameError = 'Name must be less than 100 characters'
        isValid = false
      }
      
      return isValid
    },
    
    async save() {
      if (!this.validateForm()) return
      
      this.saving = true
      this.formError = ''
      
      try {
        const payload = {
          name: this.editedItem.name.trim(),
          description: this.editedItem.description?.trim() || null,
        }
        
        await DeviceGroupsDataService.update(this.groupId, payload)
        this.$toast.success('Device group updated successfully')
        this.dialogEdit = false
        this.loadGroup()
      } catch (error) {
        this.formError = error.response?.data?.message || 'Failed to update device group'
        console.error('Error updating group:', error)
      } finally {
        this.saving = false
      }
    },
    
    async openMembersDialog() {
      await this.loadAllDevices()
      this.membersEdit = {
        deviceIds: this.group.explicitDeviceIds || [],
        companyIds: this.group.companyIds || [],
      }
      this.previewDeviceCount = this.effectiveDevices.length
      this.membersError = ''
      this.dialogMembers = true
    },
    
    async saveMembers() {
      this.savingMembers = true
      this.membersError = ''
      
      try {
        const payload = {
          deviceIds: this.membersEdit.deviceIds || [],
          companyIds: this.membersEdit.companyIds || [],
        }
        
        await DeviceGroupsDataService.updateMembers(this.groupId, payload)
        this.$toast.success('Group members updated successfully')
        this.dialogMembers = false
        this.loadGroup()
      } catch (error) {
        this.membersError = error.response?.data?.message || 'Failed to update members'
        console.error('Error updating members:', error)
      } finally {
        this.savingMembers = false
      }
    },
    
    openCommandDialog() {
      this.commandItem = {
        type: '',
        payload: null,
      }
      this.commandPayloadValues = {}
      this.payloadValid = true
      this.showPayloadErrors = false
      this.commandExpiry = { mode: 'default', ttlMinutes: null, expiresAt: null }
      this.commandError = ''
      this.commandTypeError = ''
      this.dialogCommand = true
    },
    
    // ValidationProblemDetails puts the useful text in `errors`, not `message`,
    // so flatten it rather than showing a bare "Failed to send command".
    describeSendError(error) {
      const data = error && error.response ? error.response.data : null

      if (!data) {
        return (error && error.message) || 'Failed to send command'
      }

      if (data.errors && typeof data.errors === 'object') {
        const details = Object.keys(data.errors)
          .map(field => {
            const messages = data.errors[field]
            const text = Array.isArray(messages) ? messages.join(' ') : String(messages)
            return `${field}: ${text}`
          })
          .join(' | ')

        if (details) {
          return details
        }
      }

      return data.message || data.title || data.detail || 'Failed to send command'
    },

    validateCommand() {
      this.commandTypeError = ''
      this.showPayloadErrors = false
      let isValid = true
      
      if (!this.commandItem.type) {
        this.commandTypeError = 'Command type is required'
        isValid = false
      }
      
      // The payload form reports its own validity off the catalog's required flags.
      if (!this.payloadValid) {
        this.showPayloadErrors = true
        isValid = false
      }

      // The server binds payload to JsonElement? and rejects a null one for
      // commands that declare fields, so catch it before the round trip.
      if (isValid && isPayloadIncomplete(this.selectedCommand, this.commandPayloadValues)) {
        this.commandError = `${this.commandItem.type} needs a payload. Fill in the fields above.`
        this.showPayloadErrors = true
        isValid = false
      }
      
      return isValid
    },
    
    async sendCommand() {
      if (!this.validateCommand()) return
      
      this.sendingCommand = true
      this.commandError = ''
      
      try {
        const payload = {
          type: this.commandItem.type,
          // The API binds this to JsonElement?, so send real JSON. Shape comes
          // from the catalog: none -> null, scalar -> bare value, object -> object.
          payload: buildPayloadForCommand(this.selectedCommand, this.commandPayloadValues),
          // Either ttlMinutes or expiresAt, or neither to take the per-type default.
          ...buildExpiryFields(this.commandExpiry),
        }
        
        const response = await DeviceGroupsDataService.sendCommand(this.groupId, payload)
        this.commandResult = response.data
        this.dialogCommand = false
        this.dialogCommandResult = true
        this.$toast.success('Command sent successfully')
      } catch (error) {
        this.commandError = this.describeSendError(error)
        console.error('Error sending command:', error)
      } finally {
        this.sendingCommand = false
      }
    },
    
    viewHistory() {
      this.dialogCommandResult = false
      this.$router.push(`/device-groups/${this.groupId}/history`)
    },
    
    viewDevice(item) {
      this.$router.push(`/devices?deviceId=${item.deviceId}`)
    },
    
    goBack() {
      this.$router.push('/device-groups')
    },
    
    formatDate(date) {
      if (!date) return '-'
      // Naive UTC in, local out.
      return this.$format.dateTime(date)
    },
  },
}
</script>

<style scoped>
.group-info-item {
  display: flex;
  margin-bottom: 12px;
}

.group-info-label {
  font-weight: 600;
  min-width: 140px;
  color: rgba(0, 0, 0, 0.6);
}

.group-info-value {
  flex: 1;
}

.group-stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.current-members-summary {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
}

.current-members-summary p {
  margin: 4px 0;
}
</style>
